/**
 * Ephemeral in-memory session state. One session = one in-tab demo run.
 * Nothing persists across reloads (the only state worth keeping — which macros
 * exist — is bundled and reloaded deterministically). No filesystem, no server.
 */
import { MacroRegistry } from "@macrokit/runtime";
import { AUTHORED_MACROS, type AuthoredMacroSpec } from "./authored-macros.js";
import { buildMacroFromPlan, buildPlan, type DistilledPlan, type TrajectoryStep } from "./distiller.js";
import { buildPrimitivesRegistry } from "./primitives.js";

export interface MacroCard {
  name: string;
  intent: string;
  params: string[];
  trust: "auto" | "candidate";
  proposedBy: "claude" | "heuristic";
  signature: string;
  createdAt: string;
  /** Design-time provenance shown in the UI as "how this was made". */
  trajectory: TrajectoryStep[];
  authoredNote: string;
  // --- cluster-distillation fields (undefined for pre-authored demo macros) ---
  /** How many clustered runs this macro was distilled from. */
  clusterCount?: number;
  /** Whether the macro reproduced all clustered runs under replay. */
  replayVerified?: boolean;
  /**
   * True when the macro passed replay but is not yet auto-trusted (e.g. it
   * performs a dry-run write) — eligible for the one-click "approve?" card.
   */
  pendingApproval?: boolean;
  /** Set when a runtime failure demoted the macro; flags it for re-authoring. */
  needsReauthor?: boolean;
}

export interface RequestRecord {
  id: number;
  repo: string;
  task: string;
  /**
   * "claude" = strong-model fallback (Act-2 learning runtime only).
   * "errored" = routed to a macro, but it couldn't complete (e.g. no data).
   */
  servedBy: "macro" | "unserved" | "claude" | "errored";
  macro?: string;
  trust?: "auto" | "candidate";
  costLabel: string;
  summary: string;
  steps: Array<{ tool: string; args: Record<string, unknown>; ok: boolean }>;
  ts: string;
  /** Set when this fallback run caused a cluster to graduate a new macro. */
  graduatedMacro?: string;
}

export class Session {
  readonly id: string;
  readonly primitives: MacroRegistry;
  readonly distilled: MacroRegistry;
  readonly cards: MacroCard[] = [];
  readonly history: RequestRecord[] = [];
  private readonly bySignature = new Map<string, string>();
  total = 0;
  servedByMacro = 0;
  private seq = 0;

  constructor(id: string) {
    this.id = id;
    this.primitives = buildPrimitivesRegistry();
    this.distilled = new MacroRegistry();
  }

  nextRequestId(): number {
    this.seq += 1;
    return this.seq;
  }

  hasSignature(sig: string): boolean {
    return this.bySignature.has(sig);
  }

  recordMacro(plan: DistilledPlan, provenance: { trajectory: TrajectoryStep[]; authoredNote: string }): MacroCard {
    const card: MacroCard = {
      name: plan.name,
      intent: plan.intent,
      params: plan.params.map((p) => p.name),
      trust: plan.trust,
      proposedBy: plan.proposedBy,
      signature: plan.signature,
      createdAt: new Date().toISOString(),
      trajectory: provenance.trajectory,
      authoredNote: provenance.authoredNote,
    };
    this.cards.push(card);
    this.bySignature.set(plan.signature, plan.name);
    return card;
  }

  cardFor(name: string): MacroCard | undefined {
    return this.cards.find((c) => c.name === name);
  }

  graduationPercent(): number {
    if (this.total === 0) return 0;
    return Math.round((this.servedByMacro / this.total) * 100);
  }

  snapshot() {
    return {
      sessionId: this.id,
      total: this.total,
      servedByMacro: this.servedByMacro,
      graduation: this.graduationPercent(),
      macros: this.cards,
      history: this.history,
    };
  }
}

/**
 * Register the bundled, pre-authored macros into a session. Each committed
 * trajectory is value-traced into a parameterized macro (the same distillation
 * the SDK demonstrates), then compiled to an executable macro and registered.
 */
export function loadAuthoredMacros(session: Session, names?: string[]): void {
  for (const spec of AUTHORED_MACROS) {
    if (names && !names.includes(spec.name)) continue;
    authorMacro(session, spec);
  }
}

/**
 * Author ONE macro from a recorded trajectory: value-trace it into a
 * parameterized plan, compile it, register it, and record its card. This is
 * the by-product moment — the same path loadAuthoredMacros uses per spec,
 * exposed so the UI can run it live after replaying the recorded authoring.
 * Returns the new card, or the existing card if already registered.
 */
export function authorMacro(session: Session, spec: AuthoredMacroSpec): MacroCard {
  const existing = session.cardFor(spec.name);
  if (existing) return existing;
  const plan = buildPlan(spec.taskParams, spec.trajectory, {
    name: spec.name,
    intent: spec.intent,
    proposedBy: "claude",
  });
  const macro = buildMacroFromPlan(plan, session.primitives);
  session.distilled.register(macro);
  return session.recordMacro(plan, { trajectory: spec.trajectory, authoredNote: spec.authoredNote });
}
